import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { FixturePricingState } from '@cds/betting-offer/add-ons';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { Subject, takeUntil } from 'rxjs';

import { Promotion } from '../../../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService } from '../../../crm-promotion/crm-promotion-provider.service';
import { HorsesFixtureHelper } from '../../../horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureParticipantHelper } from '../../../horse-racing-shared/services/horses-fixture-participant-helper.service';
import { FixtureUpdateService } from '../../common/fixture-update.service';
import { Events } from '../../models/events';
import { CrmPromotionMapperService } from '../../services/crm-promotion-mapper.service';
import { RacingService } from '../../services/racing-service';
import { HorsesRaceDetailsModel } from './horses-race-details.resolver';

@HooksWireup()
@Component({
    selector: 'ms-horses-race-details',
    templateUrl: 'horses-race-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HorsesRaceDetailsComponent extends OnRouteResolve<HorsesRaceDetailsModel> {
    private _fixture: WinGameFixture;
    private modelFixtureChanged = new Subject<void>();
    crmBannerPromotions: Promotion[] = [];
    isEmpty = true;
    display = false;
    timeInSeconds: number;
    runners: number;

    constructor(
        public sitecore: Sitecore,
        private changeDetector: ChangeDetectorRef,
        private dispatcher: DispatcherService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private promotionMapper: CrmPromotionMapperService,
        route: ActivatedRoute,
        private horseFixtureHelper: HorsesFixtureHelper,
        private horsesFixtureParticipantHelper: HorsesFixtureParticipantHelper,
        protected racingService: RacingService,
        protected fixtureUpdateService: FixtureUpdateService,
    ) {
        super(route);
    }

    get fixture(): WinGameFixture {
        return this._fixture;
    }

    set fixture(value: WinGameFixture) {
        this._fixture = this.racingService.filterRaceSummary(value);
        this.isEmpty = false;
        this.changeDetector.markForCheck();
    }

    onRouteResolved(model: HorsesRaceDetailsModel): void {
        this.isEmpty = true;
        if (!model || !model.fixture) {
            return;
        }

        if (typeof this.fixture !== 'undefined' && this.fixture.id !== model.fixture.id) {
            this.dispatcher.dispatch(Events.HorseRacing.CloseMeetingSelector); // Close meeting selector when going to next fixture.
            this.modelFixtureChanged.next();
        }

        this.fixture = model.fixture;
        this.fixtureUpdateService
            .handleFixtureUpdates(this.fixture)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.modelFixtureChanged))
            .subscribe((updatedFixture) => {
                this.fixture = updatedFixture;
            });

        this.timeInSeconds = DateProviderService.getTimeDifference(new Date(this.fixture.startDate), true);
        this.runners = this.horsesFixtureParticipantHelper.getRunnersCount(this.fixture);
        this.crmPromotionProvider.promotions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((promos) => this.promotionsChanged(promos));
    }

    get isResulted(): boolean {
        if (this.fixture.isOptionBet) {
            return !!this.fixture.addons.isRaceOver;
        }

        return this.fixture.addons.pricingState === FixturePricingState.Resulted;
    }

    private promotionsChanged(promotions: Promotion[]): void {
        this.crmBannerPromotions = this.promotionMapper.getBannerPromotions(this.fixture, promotions);
        this.changeDetector.markForCheck();
    }

    get isRaceOff() {
        return this.fixture.addons.pricingState === FixturePricingState.RaceOff;
    }

    updateStatus(fixture: WinGameFixture): string | undefined {
        return this.horseFixtureHelper.updateStatus(fixture);
    }

    onDisplay($event: boolean) {
        this.display = $event;
    }
}
